import { ArrowRight, CalendarRange, Infinity as Infinito, PartyPopper } from 'lucide-react';
import type { ModoCalendario, ModoFeriado } from '@miolos/core';
import {
  MODOS_CALENDARIO,
  MODOS_FERIADO,
  ROTULO_MODO_FERIADO,
  UFS,
  anosDaAgenda,
  diferencaEmDias,
  feriadosDoAno,
  formatarDataLonga,
  periodosProntos,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LinhaSwitch, Slider } from '@/components/ui/switch';
import { CartaoOpcao } from '@/components/CartaoOpcao';
import { useEditor } from '../EstadoEditor';
import { Grade, TituloPasso } from '../ui';

export function PassoCalendario() {
  const { config, atualizar, definirCalendario, documento, passos, irParaPasso } = useEditor();
  const { calendario } = config;
  const periodos = periodosProntos(calendario.ano);

  /** Atalho para a seção “Calendários extras”, onde ficam os anos do calendário. */
  const indiceDosExtras = passos.findIndex((passo) => passo.id === 'extras');

  const diasNoIntervalo = Math.max(0, diferencaEmDias(calendario.inicio, calendario.fim) + 1);
  const anosIncluidos = anosDaAgenda(calendario).length;

  /** Feriados do ano de referência, para mostrar a conta de quantos entram. */
  const feriados = feriadosDoAno(calendario.ano, config.feriados, config.uf || undefined);
  const estaduais = feriados.filter((feriado) => feriado.tipo === 'ESTADUAL').length;

  const definirModo = (modo: ModoCalendario) => definirCalendario({ modo });

  return (
    <div className="space-y-6">
      <TituloPasso
        titulo="Datado ou permanente?"
        descricao="A agenda datada já sai com dia, mês e ano impressos. A permanente tem páginas em branco para você preencher a data — e serve para qualquer ano."
      />

      <Grade>
        <CartaoOpcao
          selecionado={calendario.modo === 'DATADO'}
          titulo="Datada"
          descricao="Você define o período e cada página recebe a data, o dia da semana e a semana do ano."
          marca={<CalendarRange className="size-5 text-primary" />}
          aoSelecionar={() => definirModo('DATADO')}
        />
        <CartaoOpcao
          selecionado={calendario.modo === 'PERMANENTE'}
          titulo="Permanente (sem data)"
          descricao="Você escolhe quantas páginas de dia quer. Serve para qualquer ano e permite começar a usar no meio do caminho."
          marca={<Infinito className="size-5 text-primary" />}
          aoSelecionar={() => definirModo('PERMANENTE')}
        />
      </Grade>

      {config.paginasExtras.includes('ANO') ? (
        <section className="space-y-3 rounded-lg border border-border bg-card p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold">Anos na visão do ano</h3>
              <p className="text-xs leading-relaxed text-muted-foreground">
                A visão do ano está ligada e cobre{' '}
                <strong className="text-foreground">
                  {anosIncluidos === 1 ? 'só o ano de referência' : `${anosIncluidos} anos`}
                </strong>
                . Para incluir os anos anteriores e os seguintes — e ver o total de páginas que isso acrescenta —
                use a seção <strong className="text-foreground">Calendários extras</strong>, no passo{' '}
                <strong className="text-foreground">Páginas extras</strong>.
              </p>
            </div>
            {indiceDosExtras >= 0 ? (
              <Button variant="contorno" tamanho="pequeno" onClick={() => irParaPasso(indiceDosExtras)}>
                <ArrowRight />
                Abrir calendários extras
              </Button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Anos incluídos:</span>
            {anosDaAgenda(config.calendario).map((ano) => (
              <Badge key={ano} tom={ano === config.calendario.ano ? 'principal' : 'neutro'}>
                {ano}
                {ano === config.calendario.ano ? ' · referência' : ''}
              </Badge>
            ))}
          </div>
        </section>
      ) : (
        <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
          A visão do ano está desligada. Ligue a opção na seção{' '}
          <strong className="text-foreground">Calendários extras</strong> (passo{' '}
          <strong className="text-foreground">Páginas extras</strong>) para escolher quantos anos entram na agenda
          — antes e depois do ano de referência.
        </p>
      )}

      {calendario.modo === 'DATADO' ? (
        <section className="space-y-4 rounded-lg border border-border bg-card p-4">
          <div className="flex flex-wrap items-end gap-4">
            <Campo rotulo="Ano" dica="Usado na capa, nos divisores de mês e no calendário.">
              <Input
                type="number"
                min={1990}
                max={2100}
                value={calendario.ano}
                onChange={(evento) => definirCalendario({ ano: Number(evento.target.value) || calendario.ano })}
                className="w-28"
              />
            </Campo>
            <Campo rotulo="Início">
              <Input
                type="date"
                value={calendario.inicio}
                onChange={(evento) => definirCalendario({ inicio: evento.target.value })}
                className="w-44"
              />
            </Campo>
            <Campo rotulo="Fim">
              <Input
                type="date"
                value={calendario.fim}
                onChange={(evento) => definirCalendario({ fim: evento.target.value })}
                className="w-44"
              />
            </Campo>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Atalhos de período</p>
            <div className="flex flex-wrap gap-2">
              {periodos.map((periodo) => {
                const ativo = periodo.inicio === calendario.inicio && periodo.fim === calendario.fim;
                return (
                  <button
                    key={periodo.id}
                    type="button"
                    onClick={() => definirCalendario({ inicio: periodo.inicio, fim: periodo.fim })}
                    className={[
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                      ativo
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-card hover:border-primary/40',
                    ].join(' ')}
                  >
                    {periodo.rotulo}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge tom="principal">{diasNoIntervalo} dias no intervalo</Badge>
            <Badge>{documento.resumo.paginasDeDia} páginas de dia após o filtro de semana</Badge>
            {documento.resumo.paginasDeDia > 0 ? <Badge>{documento.resumo.totalPaginas} páginas no total</Badge> : null}
          </div>

          <p className="text-xs leading-relaxed text-muted-foreground">
            Primeiro dia gerado: {diasNoIntervalo > 0 ? formatarDataLonga(calendario.inicio) : '—'}.
          </p>
        </section>
      ) : (
        <section className="space-y-4 rounded-lg border border-border bg-card p-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">
                {config.paginasDeDia === 'SEMANA' ? 'Semanas' : 'Páginas de dia'}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {calendario.paginas} {config.paginasDeDia === 'SEMANA' ? 'semanas' : 'páginas'}
              </span>
            </div>
            <Slider
              value={[calendario.paginas]}
              min={7}
              max={400}
              step={1}
              onValueChange={([valor]) => definirCalendario({ paginas: valor ?? 180 })}
            />
            <div className="flex flex-wrap gap-2 pt-1">
              {(config.paginasDeDia === 'SEMANA' ? [26, 52, 104] : [60, 90, 120, 180, 240, 365]).map((quantidade) => (
                <button
                  key={quantidade}
                  type="button"
                  onClick={() => definirCalendario({ paginas: quantidade })}
                  className={[
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    calendario.paginas === quantidade
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card hover:border-primary/40',
                  ].join(' ')}
                >
                  {quantidade}
                </button>
              ))}
            </div>
          </div>

          <Campo
            rotulo="Ano de referência"
            dica="Usado apenas nos mini calendários e no calendário do ano."
          >
            <Input
              type="number"
              min={1990}
              max={2100}
              value={calendario.ano}
              onChange={(evento) => definirCalendario({ ano: Number(evento.target.value) || calendario.ano })}
              className="w-28"
            />
          </Campo>

          <div className="flex flex-wrap gap-2 text-xs">
            <Badge tom="principal">
              {config.paginasDeDia === 'SEMANA'
                ? `${documento.resumo.paginasDeDia} semanas`
                : `${documento.resumo.paginasDeDia} dias`}
            </Badge>
            <Badge>{documento.resumo.totalPaginas} páginas no total</Badge>
          </div>

          <p className="text-xs leading-relaxed text-muted-foreground">
            {config.paginasDeDia === 'SEMANA'
              ? 'Cada semana é uma folha com os dias escolhidos no passo “Dias da semana”, sem data impressa — você escreve a data de cada dia.'
              : 'Cada página mostra “Dia 001”, “Dia 002”... e um espaço com linhas para você escrever a data.'}
          </p>
        </section>
      )}

      <section className="space-y-4 rounded-lg border border-border bg-card p-4">
        <div className="space-y-1">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <PartyPopper className="size-4 text-primary" />
            Feriados nos mini calendários
          </h3>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Os feriados saem marcados com um ponto colorido no dia. Os móveis (Carnaval, Sexta-feira Santa e Corpus
            Christi) são calculados a partir da Páscoa, então valem para qualquer ano. Pontos facultativos saem em
            tom mais claro.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="O que marcar" dica="A lista sai nos mini calendários dos divisores de mês e da visão do ano.">
            <Select
              value={config.feriados}
              onValueChange={(valor) => atualizar({ feriados: valor as ModoFeriado })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODOS_FERIADO.map((modo) => (
                  <SelectItem key={modo} value={modo}>
                    {ROTULO_MODO_FERIADO[modo]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>

          <Campo
            rotulo="Estado (UF)"
            dica={
              config.feriados === 'ESTADUAIS'
                ? 'Entram também as datas do estado escolhido.'
                : 'Escolha “Nacionais + estaduais” para usar a UF.'
            }
          >
            <Select
              value={config.uf || 'NENHUMA'}
              disabled={config.feriados !== 'ESTADUAIS'}
              onValueChange={(valor) => atualizar({ uf: valor === 'NENHUMA' ? '' : valor })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NENHUMA">Somente nacionais</SelectItem>
                {UFS.map((uf) => (
                  <SelectItem key={uf} value={uf}>
                    {uf}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
        </div>

        <LinhaSwitch
          id={`nomes-feriados`}
          rotulo="Escrever o nome dos feriados"
          descricao="Lista as datas com o nome logo abaixo do mini calendário do mês. Na visão do ano fica desligado para não apertar a grade."
          checked={config.mostrarNomesFeriados && config.feriados !== 'NENHUM'}
          onCheckedChange={(valor) => atualizar({ mostrarNomesFeriados: valor })}
        />

        <div className="flex flex-wrap gap-2 text-xs">
          <Badge tom={feriados.length > 0 ? 'principal' : 'neutro'}>
            {feriados.length === 0
              ? 'Nenhum feriado marcado'
              : `${feriados.length} feriados em ${calendario.ano}`}
          </Badge>
          {estaduais > 0 ? <Badge>{estaduais} do estado {config.uf}</Badge> : null}
        </div>
      </section>
    </div>
  );
}
